import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  List,
  Card,
  Spin,
  message,
  Typography,
  Row,
  Col,
  Upload,
  Alert,
  Modal,
  Tooltip,
  Popconfirm,
  Space,
  Divider,
  Tag,
  Form,
  DatePicker,
  Select,
  InputNumber,
  Drawer
} from 'antd';
import {
  SearchOutlined,
  UploadOutlined,
  PictureOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  LeftOutlined,
  RightOutlined,
  CloseOutlined,
  UserAddOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { createClient } from '@supabase/supabase-js';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell
} from 'recharts';

const { Title, Text } = Typography;
const { Option } = Select;

const supabaseUrl = 'https://vlakljvouimjemxbcnmo.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZsYWtsanZvdWltamVteGJjbm1vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MjY2MTIxMTAsImV4cCI6MjA0MjE4ODExMH0.eDEDBU8MJraE3XkpcbvviZQEfUD06xq4qVF0KW1kskA';
const supabase = createClient(supabaseUrl, supabaseKey);

const CharterLookup = () => {
  const [clientName, setClientName] = useState('');
  const [clientInfo, setClientInfo] = useState(null);
  const [charters, setCharters] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [clientImages, setClientImages] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  
  // New state for client search and selection
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [allClients, setAllClients] = useState([]);
  const [allClientsLoading, setAllClientsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const pageSize = 10;
  
  // New state for drawers
  const [addCustomerDrawerVisible, setAddCustomerDrawerVisible] = useState(false);
  const [addCharterDrawerVisible, setAddCharterDrawerVisible] = useState(false);
  const [customerForm] = Form.useForm();
  const [charterForm] = Form.useForm();

  const paymentColors = {
    'CASH': '#52c41a',       // Bright green
    'CB': '#1890ff',         // Bright blue (assuming CB means credit card)
    'CB + CASH': '#722ed1',  // Purple (for combined payment)
    'Check': '#faad14',      // Amber
    'Venmo': '#13c2c2',      // Cyan
    'PayPal': '#eb2f96',     // Pink
    'Other': '#8c8c8c',      // Gray
  };
  
  const getPaymentMethodColor = (method) => {
    if (!method) return paymentColors['Other'];
    return paymentColors[method] || paymentColors['Other'];
  };

  // Check Supabase connection and load initial client list
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('client_id')
          .limit(1);
        
        if (error) {
          console.error('Supabase connection error:', error);
          message.error(`Database connection error: ${error.message}`);
        } else {
          console.log('Supabase connection successful');
          // Load initial client list
          loadAllClients();
        }
      } catch (err) {
        console.error('Unexpected error checking connection:', err);
      }
    };
    
    checkConnection();
  }, []);

  const loadAllClients = async (page = 1) => {
    setAllClientsLoading(true);
    
    try {
      // Get total count
      const { count } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true });

      setTotalClients(count || 0);

      // Get paginated clients
      const { data: clients, error } = await supabase
        .from('clients')
        .select('client_id, client_name, email, phone, created_at')
        .order('client_name', { ascending: true })
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (error) {
        throw new Error(error.message);
      }

      setAllClients(clients || []);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading clients:', err);
      message.error(`Failed to load clients: ${err.message}`);
      setAllClients([]);
    } finally {
      setAllClientsLoading(false);
    }
  };

  const searchClients = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    
    try {
      const { data: clients, error } = await supabase
        .from('clients')
        .select('client_id, client_name, email, phone')
        .ilike('client_name', `%${searchTerm}%`)
        .order('client_name')
        .limit(10);

      if (error) {
        throw new Error(error.message);
      }

      setSearchResults(clients || []);
    } catch (err) {
      console.error('Error searching clients:', err);
      message.error(`Failed to search clients: ${err.message}`);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const selectClient = async (client) => {
    setClientInfo(client);
    setClientName(client.client_name);
    setSearchResults([]); // Clear search results
    await fetchClientDetails(client.client_id);
  };

  const handleDeleteClient = async (clientId, clientName) => {
    try {
      // First check if client has charters
      const { data: charters, error: charterError } = await supabase
        .from('charters')
        .select('charter_id')
        .eq('client_id', clientId);

      if (charterError) {
        throw new Error(charterError.message);
      }

      if (charters && charters.length > 0) {
        message.error(`Cannot delete ${clientName}. This client has ${charters.length} charter(s). Please delete all charters first.`);
        return;
      }

      // Check if client has images
      const { data: images, error: imagesError } = await supabase
        .from('client_images')
        .select('image_id, image_path')
        .eq('client_id', clientId);

      if (imagesError) {
        console.warn('Error checking client images:', imagesError);
      }

      // Delete client images if any exist
      if (images && images.length > 0) {
        // Delete from storage
        const imagePaths = images.map(img => img.image_path);
        const { error: storageError } = await supabase.storage
          .from('client-photos')
          .remove(imagePaths);

        if (storageError) {
          console.warn('Some images could not be deleted from storage:', storageError);
        }

        // Delete from database
        const { error: imageDbError } = await supabase
          .from('client_images')
          .delete()
          .eq('client_id', clientId);

        if (imageDbError) {
          console.warn('Some image records could not be deleted:', imageDbError);
        }
      }

      // Finally delete the client
      const { error: clientError } = await supabase
        .from('clients')
        .delete()
        .eq('client_id', clientId);

      if (clientError) {
        throw new Error(clientError.message);
      }

      message.success(`Client "${clientName}" deleted successfully`);
      
      // Refresh the client list
      loadAllClients(currentPage);
      
      // Clear selection if this was the selected client
      if (clientInfo && clientInfo.client_id === clientId) {
        setClientInfo(null);
        setClientName('');
        setCharters([]);
        setClientImages([]);
        setTotalSpent(0);
      }
    } catch (err) {
      console.error('Error deleting client:', err);
      message.error(`Failed to delete client: ${err.message}`);
    }
  };

  const fetchClientDetails = async (clientId) => {
    setLoading(true);
    setCharters([]);
    setClientImages([]);
    setTotalSpent(0);
    setUploadError(null);

    try {
      // Get full client info
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('*')
        .eq('client_id', clientId)
        .single();

      if (clientError) {
        throw new Error(clientError.message);
      }

      setClientInfo(clientData);
      setNotes(clientData.notes || '');

      // Get client images
      const { data: images, error: imagesError } = await supabase
        .from('client_images')
        .select('*')
        .eq('client_id', clientId)
        .order('is_primary', { ascending: false })
        .order('upload_date', { ascending: false });

      if (imagesError) {
        console.error('Error fetching images:', imagesError);
        message.warning(`Could not load client images: ${imagesError.message}`);
      } else if (images && images.length > 0) {
        const imagesWithUrls = await Promise.all(
          images.map(async (image) => {
            try {
              const { data, error } = await supabase.storage
                .from('client-photos')
                .createSignedUrl(image.image_path, 3600);
              
              return {
                ...image,
                signedUrl: error ? null : data.signedUrl
              };
            } catch (err) {
              console.error(`Error getting URL for image ${image.image_id}:`, err);
              return { ...image, signedUrl: null };
            }
          })
        );
        
        setClientImages(imagesWithUrls.filter(img => img.signedUrl !== null));
      }

      // Get client charters
      const { data: chartersData, error: charterError } = await supabase
        .from('charters')
        .select('*')
        .eq('client_id', clientId)
        .order('charter_date', { ascending: false });

      if (charterError) {
        throw new Error(charterError.message);
      }

      if (chartersData) {
        setCharters(chartersData);
        const total = chartersData.reduce(
          (sum, c) => sum + (parseFloat(c.total_amount) || 0),
          0
        );
        setTotalSpent(total);
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
      message.error(`Failed to load client details: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (values) => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .insert([
          {
            client_name: values.client_name,
            email: values.email,
            phone: values.phone,
            contact_info: values.contact_info,
            notes: values.notes,
            other_info: values.other_info
          }
        ])
        .select();

      if (error) {
        throw new Error(error.message);
      }

      message.success('Customer added successfully');
      setAddCustomerDrawerVisible(false);
      customerForm.resetFields();
      
      // Auto-select the new customer
      setClientName(values.client_name);
      await selectClient(data[0]);
      
      // Refresh the all clients list
      loadAllClients(currentPage);
    } catch (err) {
      console.error('Error adding customer:', err);
      message.error(`Failed to add customer: ${err.message}`);
    }
  };

  const handleAddCharter = async (values) => {
    if (!clientInfo) {
      message.error('Please select a client first');
      return;
    }

    try {
      const charterData = {
        client_id: clientInfo.client_id,
        charter_type: values.charter_type,
        charter_date: values.charter_date ? values.charter_date.format('YYYY-MM-DD') : null,
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : null,
        total_amount: values.total_amount,
        cash_amount: values.cash_amount,
        payment_method: values.payment_method,
        charterer_name: values.charterer_name,
        notes: values.notes
      };

      const { data, error } = await supabase
        .from('charters')
        .insert([charterData])
        .select();

      if (error) {
        throw new Error(error.message);
      }

      message.success('Charter added successfully');
      setAddCharterDrawerVisible(false);
      charterForm.resetFields();
      
      // Refresh the charters list
      await fetchClientDetails(clientInfo.client_id);
    } catch (err) {
      console.error('Error adding charter:', err);
      message.error(`Failed to add charter: ${err.message}`);
    }
  };

  const handleSaveClientInfo = async () => {
    if (!clientInfo) {
      console.log('No client info to save');
      return;
    }
    
    setSavingNotes(true);
    console.log('Client info before update:', clientInfo);
    console.log('Notes value:', notes);

    try {
      const updatePayload = {
        client_name: clientInfo.client_name
      };
      
      if ('email' in clientInfo) updatePayload.email = clientInfo.email;
      if ('phone' in clientInfo) updatePayload.phone = clientInfo.phone;
      if (notes !== undefined) updatePayload.notes = notes;
      if ('other_info' in clientInfo) updatePayload.other_info = clientInfo.other_info;
      
      console.log('Updating client with payload:', updatePayload);

      const { data, error } = await supabase
        .from('clients')
        .update(updatePayload)
        .eq('client_id', clientInfo.client_id)
        .select();

      if (error) {
        console.error('Update error details:', error);
        throw new Error(error.message);
      }

      console.log('Update response:', data);
      message.success('Client info updated');
    } catch (err) {
      console.error('Error saving client info:', err);
      message.error(`Failed to update client info: ${err.message}`);
    } finally {
      setSavingNotes(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!clientInfo) {
      message.error('No client selected');
      return false;
    }
    
    setUploadModalVisible(false);
    setUploading(true);
    setUploadError(null);

    try {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('You can only upload image files');
        setUploading(false);
        return false;
      }
      
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('Image must be smaller than 5MB');
        setUploading(false);
        return false;
      }

      const fileExt = file.name.split('.').pop();
      const timestamp = Date.now();
      const filePath = `client_${clientInfo.client_id}_${timestamp}.${fileExt}`;

      const { data, error: uploadError } = await supabase.storage
        .from('client-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        setUploadError(`Upload failed: ${uploadError.message}`);
        return false;
      }

      const { data: urlData, error: urlError } = await supabase.storage
        .from('client-photos')
        .createSignedUrl(filePath, 3600);

      if (urlError) {
        console.error('URL generation error:', urlError);
        setUploadError(`Could not get photo URL: ${urlError.message}`);
        return false;
      }

      const isPrimary = clientImages.length === 0;

      const { data: imageData, error: imageError } = await supabase
        .from('client_images')
        .insert([
          {
            client_id: clientInfo.client_id,
            image_path: filePath,
            caption: uploadCaption || `Image ${clientImages.length + 1}`,
            is_primary: isPrimary
          }
        ])
        .select();

      if (imageError) {
        console.error('Database insert error:', imageError);
        setUploadError(`Failed to save image to database: ${imageError.message}`);
        
        await supabase.storage
          .from('client-photos')
          .remove([filePath]);
          
        return false;
      }

      const newImage = {
        ...imageData[0],
        signedUrl: urlData.signedUrl
      };

      setClientImages(prev => [newImage, ...prev]);
      setUploadCaption('');
      message.success('Photo uploaded successfully');
      
      return false;
    } catch (err) {
      console.error('Unexpected upload error:', err);
      setUploadError(`An unexpected error occurred: ${err.message}`);
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId, imagePath) => {
    try {
      console.log(`Attempting to delete image with ID: ${imageId} and path: ${imagePath}`);
      
      const { error: dbError } = await supabase
        .from('client_images')
        .delete()
        .eq('image_id', imageId);

      if (dbError) {
        console.error('Database deletion error:', dbError);
        throw new Error(dbError.message);
      }

      console.log('Database record deleted successfully');

      const { error: storageError } = await supabase.storage
        .from('client-photos')
        .remove([imagePath]);

      if (storageError) {
        console.warn('Storage removal warning:', storageError);
      } else {
        console.log('Storage file deleted successfully');
      }

      setClientImages(prev => prev.filter(img => img.image_id !== imageId));
      message.success('Image deleted');
      
      if (galleryVisible && clientImages.length <= 1) {
        setGalleryVisible(false);
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      message.error(`Failed to delete image: ${err.message}`);
    }
  };

  const handleSetPrimaryImage = async (imageId) => {
    try {
      const { error: resetError } = await supabase
        .from('client_images')
        .update({ is_primary: false })
        .eq('client_id', clientInfo.client_id);

      if (resetError) {
        throw new Error(resetError.message);
      }

      const { error: updateError } = await supabase
        .from('client_images')
        .update({ is_primary: true })
        .eq('image_id', imageId);

      if (updateError) {
        throw new Error(updateError.message);
      }

      setClientImages(prev => 
        prev.map(img => ({
          ...img,
          is_primary: img.image_id === imageId
        }))
      );
      
      message.success('Primary image updated');
    } catch (err) {
      console.error('Error setting primary image:', err);
      message.error(`Failed to set primary image: ${err.message}`);
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => 
      prev === 0 ? clientImages.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => 
      prev === clientImages.length - 1 ? 0 : prev + 1
    );
  };

  const openGallery = (startIndex = 0) => {
    setCurrentImageIndex(startIndex);
    setGalleryVisible(true);
  };

  const renderGallery = () => {
    if (!galleryVisible || clientImages.length === 0) return null;

    const currentImage = clientImages[currentImageIndex];

    return (
      <Modal
        visible={galleryVisible}
        footer={null}
        onCancel={() => setGalleryVisible(false)}
        width="80%"
        centered
        closeIcon={<CloseOutlined style={{ color: 'white', fontSize: '20px' }} />}
        bodyStyle={{ 
          padding: 0, 
          backgroundColor: 'black',
          height: '80vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div style={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          width: '100%', 
          padding: '10px 20px',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          zIndex: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Text style={{ color: 'white' }}>
            {currentImage.caption || `Image ${currentImageIndex + 1} of ${clientImages.length}`}
          </Text>
          
          <div>
            <Space>
              {!currentImage.is_primary && (
                <Button 
                  type="text" 
                  icon={<StarOutlined />} 
                  style={{ color: 'white' }}
                  onClick={() => handleSetPrimaryImage(currentImage.image_id)}
                  title="Set as primary image"
                />
              )}
              
              <Popconfirm
                title="Delete this image?"
                description="This action cannot be undone."
                onConfirm={() => {
                  handleDeleteImage(currentImage.image_id, currentImage.image_path);
                  if (clientImages.length <= 1) {
                    setGalleryVisible(false);
                  }
                }}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button 
                  type="text" 
                  danger
                  icon={<DeleteOutlined />}
                  title="Delete this image"
                />
              </Popconfirm>
            </Space>
          </div>
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: '100%',
          position: 'relative'
        }}>
          <Button 
            icon={<LeftOutlined />} 
            style={{ 
              position: 'absolute', 
              left: 10, 
              zIndex: 2,
              opacity: 0.7
            }}
            onClick={handlePrevImage}
          />
          
          <img 
            src={currentImage.signedUrl} 
            alt={currentImage.caption || `Client Image ${currentImageIndex + 1}`}
            style={{ 
              maxHeight: '90%', 
              maxWidth: '90%', 
              objectFit: 'contain'
            }} 
          />
          
          <Button 
            icon={<RightOutlined />} 
            style={{ 
              position: 'absolute', 
              right: 10, 
              zIndex: 2,
              opacity: 0.7
            }}
            onClick={handleNextImage}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2}>Cardex</Title>

      {/* Main Search Bar with Action Buttons */}
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col flex="auto">
          <Input
            placeholder="Search for client by name..."
            value={clientName}
            onChange={(e) => {
              setClientName(e.target.value);
              searchClients(e.target.value);
            }}
            suffix={
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => searchClients(clientName)}
                loading={searchLoading}
              >
                Search
              </Button>
            }
          />
        </Col>
        <Col>
          <Button 
            type="primary" 
            icon={<UserAddOutlined />}
            onClick={() => setAddCustomerDrawerVisible(true)}
          >
            Add Customer
          </Button>
        </Col>
        {clientInfo && (
          <Col>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setAddCharterDrawerVisible(true)}
            >
              Add Charter
            </Button>
          </Col>
        )}
      </Row>

      {/* Search Results List */}
      {searchResults.length > 0 && (
        <Card 
          title="Search Results" 
          style={{ marginBottom: 16 }}
          size="small"
        >
          <List
            size="small"
            dataSource={searchResults}
            renderItem={(client) => (
              <List.Item 
                style={{ 
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s'
                }}
                onClick={() => selectClient(client)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <List.Item.Meta
                  title={<strong>{client.client_name}</strong>}
                  description={
                    <Space direction="vertical" size={0}>
                      {client.email && <Text type="secondary">{client.email}</Text>}
                      {client.phone && <Text type="secondary">{client.phone}</Text>}
                    </Space>
                  }
                />
                <Button type="link" size="small">
                  Select →
                </Button>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* All Clients List with Pagination */}
      {!clientInfo && searchResults.length === 0 && (
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>All Clients ({totalClients})</span>
              <Text type="secondary" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                Click on a client to view details
              </Text>
            </div>
          }
          style={{ marginBottom: 16 }}
          loading={allClientsLoading}
        >
          <List
            size="small"
            dataSource={allClients}
            locale={{ emptyText: 'No clients found' }}
            renderItem={(client) => (
              <List.Item 
                style={{ 
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  border: '1px solid #f0f0f0',
                  marginBottom: '8px'
                }}
                onClick={() => selectClient(client)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#d0d7de';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#f0f0f0';
                }}
                actions={[
                  <Popconfirm
                    key="delete"
                    title="Delete this client?"
                    description={
                      <div>
                        <p>Are you sure you want to delete <strong>{client.client_name}</strong>?</p>
                        <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                          This will also delete all associated images. 
                          Charters must be deleted separately first.
                        </p>
                      </div>
                    }
                    onConfirm={(e) => {
                      e.stopPropagation();
                      handleDeleteClient(client.client_id, client.client_name);
                    }}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    placement="topRight"
                  >
                    <Button 
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      title="Delete client"
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={<strong style={{ color: '#1890ff' }}>{client.client_name}</strong>}
                  description={
                    <Space direction="vertical" size={0}>
                      {client.email && <Text type="secondary">{client.email}</Text>}
                      {client.phone && <Text type="secondary">{client.phone}</Text>}
                      {client.created_at && (
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Created: {new Date(client.created_at).toLocaleDateString()}
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
            pagination={{
              current: currentPage,
              total: totalClients,
              pageSize: pageSize,
              onChange: (page) => loadAllClients(page),
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} clients`,
              style: { textAlign: 'center', marginTop: '16px' }
            }}
          />
        </Card>
      )}

      {/* Selected Client Header */}
      {clientInfo && (
        <Card style={{ marginBottom: 16, backgroundColor: '#f0f9ff', border: '1px solid #1890ff' }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
                Selected Client: {clientInfo.client_name}
              </Title>
              {clientInfo.email && <Text type="secondary">{clientInfo.email}</Text>}
              {clientInfo.phone && <Text type="secondary"> • {clientInfo.phone}</Text>}
            </Col>
            <Col>
              <Button 
                onClick={() => {
                  setClientInfo(null);
                  setClientName('');
                  setCharters([]);
                  setClientImages([]);
                  setTotalSpent(0);
                }}
              >
                Clear Selection
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {loading ? (
        <Spin tip="Loading data..." style={{ marginTop: 24 }} />
      ) : (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          {/* LEFT COLUMN */}
          <Col xs={24} md={14}>
            {charters.length > 0 && (
              <>
                <Card style={{ marginBottom: 24 }}>
                  <Title level={4}>Total Spent: ${totalSpent.toFixed(2)}</Title>
                </Card>

                <Card title="Charters" style={{ marginBottom: 24 }}>
                  <List
                    size="small"
                    dataSource={charters}
                    locale={{ emptyText: 'No charters found' }}
                    renderItem={(charter) => (
                      <List.Item 
                        style={{ 
                          padding: '12px 16px',
                          backgroundColor: '#fafafa', 
                          marginBottom: '8px',
                          borderRadius: '4px'
                        }}
                      >
                        <Row
                          style={{ width: '100%' }}
                          justify="space-between"
                          align="middle"
                        >
                          <Col flex="auto" span={8}>
                            <strong style={{ fontSize: '15px' }}>{charter.charter_type}</strong>
                          </Col>
                          <Col span={5} style={{ textAlign: 'center' }}>
                            {charter.charter_date || 'TBD'}
                          </Col>
                          <Col span={4} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                            ${parseFloat(charter.total_amount || 0).toFixed(2)}
                          </Col>
                          <Col span={5} style={{ textAlign: 'right' }}>
                            <Tag
                              color={getPaymentMethodColor(charter.payment_method)}
                              style={{ 
                                padding: '4px 8px', 
                                fontSize: '14px',
                                borderRadius: '4px'
                              }}
                            >
                              {charter.payment_method || 'N/A'}
                            </Tag>
                          </Col>
                        </Row>
                      </List.Item>
                    )}
                  />
                </Card>

                {charters.length > 1 && (
                  <Card title="Charter Spending Over Time">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={charters}
                        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="charter_date" />
                        <YAxis />
                        <RechartsTooltip 
                          formatter={(value, name, props) => {
                            const charter = props.payload;
                            return [`$${value} (${charter.payment_method || 'N/A'})`, 'Amount'];
                          }}
                        />
                        <Bar dataKey="total_amount">
                          {charters.map((entry, index) => {
                            const method = entry.payment_method || 'Other';
                            const color = paymentColors[method] || paymentColors['Other'];
                            return <Cell key={`cell-${index}`} fill={color} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    
                    <div style={{ marginTop: 16 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {Array.from(new Set(charters.map(c => c.payment_method || 'N/A')))
                          .map(method => (
                            <Tag 
                              key={method}
                              color={paymentColors[method] || paymentColors['Other']}
                              style={{ padding: '4px 8px' }}
                            >
                              {method}
                            </Tag>
                          ))}
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}
          </Col>

          {/* RIGHT COLUMN */}
          <Col xs={24} md={10}>
            {clientInfo && (
              <>
                {/* Client Photos Gallery Card */}
                <Card 
                  title="Client Photos" 
                  style={{ marginBottom: 24 }}
                  extra={
                    <Button 
                      type="primary" 
                      icon={<UploadOutlined />}
                      onClick={() => setUploadModalVisible(true)}
                    >
                      Add Image
                    </Button>
                  }
                >
                  {clientImages.length > 0 ? (
                    <>
                      <Row gutter={[8, 8]}>
                        {clientImages.map((image, index) => (
                          <Col span={8} key={image.image_id}>
                            <Card
                              hoverable
                              style={{ 
                                width: '100%',
                                height: '150px',
                                overflow: 'hidden'
                              }}
                              bodyStyle={{ padding: 0 }}
                              cover={
                                <div 
                                  style={{
                                    height: '100px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative'
                                  }}
                                  onClick={() => openGallery(index)}
                                >
                                  <img
                                    src={image.signedUrl}
                                    alt={image.caption || `Client Image ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                  />
                                  {image.is_primary && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        right: 0,
                                        background: 'rgba(24, 144, 255, 0.7)',
                                        padding: '2px 6px',
                                        borderBottomLeftRadius: '4px'
                                      }}
                                    >
                                      <StarFilled style={{ color: 'white', fontSize: '12px' }} />
                                    </div>
                                  )}
                                </div>
                              }
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
                                <Button 
                                  type={image.is_primary ? "primary" : "default"}
                                  size="small"
                                  icon={image.is_primary ? <StarFilled /> : <StarOutlined />}
                                  onClick={() => handleSetPrimaryImage(image.image_id)}
                                >
                                  {image.is_primary ? "Primary" : "Set Primary"}
                                </Button>
                                
                                <Popconfirm
                                  title="Delete this image?"
                                  description="This action cannot be undone."
                                  onConfirm={() => handleDeleteImage(image.image_id, image.image_path)}
                                  okText="Yes, Delete"
                                  cancelText="Cancel"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button 
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                  >
                                    Delete
                                  </Button>
                                </Popconfirm>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                      <Divider />
                      <Button 
                        type="link" 
                        onClick={() => openGallery(0)}
                        icon={<PictureOutlined />}
                      >
                        View All Images
                      </Button>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                      <Text type="secondary">No images for this client yet</Text>
                      <p>
                        <Button 
                          type="primary"
                          icon={<UploadOutlined />}
                          onClick={() => setUploadModalVisible(true)}
                          style={{ marginTop: 8 }}
                        >
                          Add First Image
                        </Button>
                      </p>
                    </div>
                  )}
                </Card>

                {/* Client Info Card */}
                <Card title="Edit Client Information">
                  {uploadError && (
                    <Alert 
                      message="Upload Error" 
                      description={uploadError}
                      type="error" 
                      showIcon 
                      closable
                      style={{ marginBottom: 16 }}
                      onClose={() => setUploadError(null)}
                    />
                  )}

                  <p style={{ marginTop: 16 }}><strong>Name:</strong></p>
                  <Input
                    value={clientInfo.client_name}
                    onChange={(e) =>
                      setClientInfo({ ...clientInfo, client_name: e.target.value })
                    }
                  />

                  <p style={{ marginTop: 16 }}><strong>Email:</strong></p>
                  <Input
                    value={clientInfo.email || ''}
                    onChange={(e) =>
                      setClientInfo({ ...clientInfo, email: e.target.value })
                    }
                  />

                  <p style={{ marginTop: 16 }}><strong>Phone:</strong></p>
                  <Input
                    value={clientInfo.phone || ''}
                    onChange={(e) =>
                      setClientInfo({ ...clientInfo, phone: e.target.value })
                    }
                  />

                  <Title level={5} style={{ marginTop: 24 }}>Client Notes</Title>
                  <Input.TextArea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Enter notes about this client..."
                  />

                  <Button
                    type="primary"
                    onClick={handleSaveClientInfo}
                    loading={savingNotes}
                    style={{ marginTop: 16 }}
                  >
                    Save Client Info
                  </Button>
                </Card>
              </>
            )}
          </Col>
        </Row>
      )}

      {/* Add Customer Drawer */}
      <Drawer
        title="Add New Customer"
        width={400}
        onClose={() => {
          setAddCustomerDrawerVisible(false);
          customerForm.resetFields();
        }}
        visible={addCustomerDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={() => {
              setAddCustomerDrawerVisible(false);
              customerForm.resetFields();
            }}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={() => customerForm.submit()}
            >
              Add Customer
            </Button>
          </Space>
        }
      >
        <Form
          form={customerForm}
          layout="vertical"
          onFinish={handleAddCustomer}
          hideRequiredMark
        >
          <Form.Item
            name="client_name"
            label="Client Name"
            rules={[{ required: true, message: 'Please enter client name' }]}
          >
            <Input placeholder="Enter client name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="contact_info"
            label="Contact Info"
          >
            <Input.TextArea rows={3} placeholder="Additional contact information" />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Notes about this client" />
          </Form.Item>

          <Form.Item
            name="other_info"
            label="Other Information"
          >
            <Input.TextArea rows={3} placeholder="Other relevant information" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Add Charter Drawer */}
      <Drawer
        title="Add New Charter"
        width={500}
        onClose={() => {
          setAddCharterDrawerVisible(false);
          charterForm.resetFields();
        }}
        visible={addCharterDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={() => {
              setAddCharterDrawerVisible(false);
              charterForm.resetFields();
            }}>
              Cancel
            </Button>
            <Button 
              type="primary" 
              onClick={() => charterForm.submit()}
            >
              Add Charter
            </Button>
          </Space>
        }
      >
        <Form
          form={charterForm}
          layout="vertical"
          onFinish={handleAddCharter}
          hideRequiredMark
        >
          <Form.Item
            name="charter_type"
            label="Charter Type"
            rules={[{ required: true, message: 'Please enter charter type' }]}
          >
            <Input placeholder="e.g., Sunset Cruise, Fishing Charter, etc." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="charter_date"
                label="Charter Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="purchase_date"
                label="Purchase Date"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="total_amount"
                label="Total Amount"
                rules={[{ required: true, message: 'Please enter total amount' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  step={0.01}
                  precision={2}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cash_amount"
                label="Cash Amount"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  step={0.01}
                  precision={2}
                  formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="payment_method"
                label="Payment Method"
              >
                <Select placeholder="Select payment method">
                  <Option value="CASH">Cash</Option>
                  <Option value="CB">Credit Card</Option>
                  <Option value="CB + CASH">Credit Card + Cash</Option>
                  <Option value="Check">Check</Option>
                  <Option value="Venmo">Venmo</Option>
                  <Option value="PayPal">PayPal</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="charterer_name"
                label="Charterer Name"
              >
                <Input placeholder="Name of person chartering" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Notes about this charter" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Upload Image Modal */}
      <Modal
        title="Upload New Image"
        visible={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
      >
        <Input
          placeholder="Image Caption (optional)"
          value={uploadCaption}
          onChange={(e) => setUploadCaption(e.target.value)}
          style={{ marginBottom: 16 }}
        />
        
        <Upload
          name="file"
          beforeUpload={handlePhotoUpload}
          showUploadList={false}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />} loading={uploading} type="primary" block>
            Select and Upload Image
          </Button>
        </Upload>
        
        <div style={{ marginTop: 16, color: '#999' }}>
          <Text type="secondary">
            Supported formats: JPG, PNG, GIF (max 5MB)
          </Text>
        </div>
      </Modal>

      {/* Image Gallery Modal */}
      {renderGallery()}
    </div>
  );
};

export default CharterLookup;